import * as React from 'react';
import {Eye, Minus, Plus, X} from 'lucide-react';
import {useTranslation} from 'react-i18next';
import {cn} from '../../lib/cn';
import {useCombinedRef} from '../../hooks';
import {usePrevious} from '../../hooks/use-previous';
import {useInputValueState} from './useInputValueState';
import {FieldErrorMessage} from '../../field-error-message';
import {Fieldset} from '../_fieldset';

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label?: string | React.ReactNode;
  topLabel?: string;
  tooltip?: React.ReactNode;
  optional?: boolean | string;
  error?: unknown;
  invalid?: boolean;
  loading?: boolean;
  empty?: boolean;
  helperText?: string;
  footer?: string | React.ReactNode;
  leftIcon?: React.ReactNode;
  sign?: string | React.ReactNode;
  onReset?: () => void;
  showNumberButtons?: boolean;
  onIncrement?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onDecrement?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  wrapperClassName?: string;
  fieldClassName?: string;
  contentClassName?: string;
  inputClassName?: string;
  trailingAdornment?: React.ReactNode;
  renderErrorMessage?: boolean;
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      value,
      defaultValue,
      className,
      wrapperClassName,
      fieldClassName,
      contentClassName,
      inputClassName,
      label,
      topLabel,
      tooltip,
      disabled,
      loading,
      readOnly,
      name,
      id,
      type,
      error,
      optional = false,
      invalid,
      empty,
      showNumberButtons,
      onIncrement,
      onDecrement,
      sign,
      footer,
      onReset,
      onChange,
      onFocus,
      onBlur,
      helperText,
      placeholder,
      leftIcon,
      trailingAdornment,
      renderErrorMessage = true,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const inputId = id ?? name ?? generatedId;
    const errorId = `${inputId}-error`;
    const {t} = useTranslation();
    const {inputRef, isControlled, isEmpty, setNativeValue, syncValueState} =
      useInputValueState({
        empty,
        value,
        defaultValue,
      });
    const [inputType, setInputType] = React.useState(type);
    const [isPasswordRevealed, setIsPasswordRevealed] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(false);
    const combinedInputRef = useCombinedRef(inputRef, ref);
    const prevInputType = usePrevious(inputType);
    const isPasswordReveal =
      (prevInputType === 'password' || type === 'password') && !isEmpty;
    const hasInvalidState = Boolean(invalid) || (Boolean(error) && error !== 'NONE');
    const errorMessage =
      typeof error === 'string' && error !== 'NONE' ? error : undefined;

    const togglePasswordReveal = () => {
      if (isPasswordRevealed) {
        setInputType('password');
        setIsPasswordRevealed(false);
      } else {
        setInputType('text');
        setIsPasswordRevealed(true);
      }
    };

    React.useEffect(() => {
      setInputType(type);
    }, [type]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (readOnly || disabled) return;
      if (!isControlled) {
        setNativeValue(event.currentTarget.value);
      }
      onChange?.(event);
    };

    const handleLabelClick = () => {
      if (readOnly || disabled || loading) return;
      inputRef.current?.focus();
    };

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      if (readOnly || disabled) return;
      onFocus?.(event);
      syncValueState();
      setIsFocused(true);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(event);
      syncValueState();
      setIsFocused(false);
    };

    const showRightPaddingForReset = Boolean(onReset);
    const showRightPaddingForReveal = isPasswordReveal;

    return (
      <div
        className={cn(
          'input',
          disabled && 'input--disabled',
          loading && 'input--loading',
          readOnly && 'input--readonly',
          isFocused && 'input--focused',
          isEmpty && 'input--empty',
          hasInvalidState && 'input--invalid',
          'relative block w-[var(--field-width,100%)] max-w-[var(--field-max-width,296px)] min-h-[var(--field-min-height,48px)]',
          disabled && 'cursor-not-allowed opacity-50',
          loading && 'cursor-progress opacity-50',
          wrapperClassName,
          className,
        )}
      >
        {topLabel && (
          <label
            htmlFor={inputId}
            className="input__top-label mb-2 block text-[length:var(--field-top-label-font-size)] font-[var(--field-top-label-font-weight)] text-[var(--chekin-color-brand-navy)]"
          >
            {topLabel}
          </label>
        )}
        <div className={cn('input__field', 'relative block w-full', fieldClassName)}>
          <div
            className={cn(
              'input__content relative w-full cursor-text',
              readOnly && !disabled && !loading && 'cursor-default',
              contentClassName,
            )}
          >
            <Fieldset
              isFocused={isFocused && !readOnly}
              invalid={hasInvalidState}
              isEmpty={isEmpty}
              onClick={handleLabelClick}
              isActivated={!isEmpty || isFocused}
              readOnly={readOnly}
              loading={loading}
              disabled={disabled}
              htmlFor={inputId}
              labelId={`${inputId}-label`}
              legend={typeof label === 'string' ? label : undefined}
              label={label}
              tooltip={tooltip}
              className="input__fieldset"
              labelClassName={cn({
                input__label: true,
                'pl-[28px]': !!leftIcon,
              })}
            />
            {leftIcon && (
              <span className="input__left-icon pointer-events-none absolute left-0 top-0 flex h-full max-w-10 items-center justify-center text-[var(--chekin-color-gray-2)]">
                <span className="input__left-icon-inner flex h-full w-10 items-center justify-center">
                  {leftIcon}
                </span>
              </span>
            )}
            <input
              {...props}
              ref={combinedInputRef}
              id={inputId}
              name={name}
              type={inputType}
              data-testid="input"
              value={value}
              defaultValue={defaultValue}
              disabled={disabled || loading}
              readOnly={readOnly}
              required={!optional}
              aria-label={typeof label === 'string' ? label : undefined}
              aria-invalid={hasInvalidState}
              aria-busy={loading}
              aria-describedby={errorMessage && renderErrorMessage ? errorId : undefined}
              placeholder={isFocused || !label ? placeholder : undefined}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              className={cn(
                'input__control',
                'm-0 box-border h-12 w-full rounded-[6px] border-0 px-4 text-[16px] font-medium leading-5 text-[var(--chekin-color-brand-navy)] outline-none transition-colors duration-200 [text-overflow:ellipsis] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
                'placeholder:text-[length:var(--field-placeholder-font-size)] placeholder:font-[var(--field-placeholder-font-weight)] placeholder:text-[var(--chekin-color-gray-1)] placeholder:opacity-100',
                isEmpty && !isFocused
                  ? 'bg-[var(--empty-field-background)]'
                  : 'bg-transparent',
                isEmpty && 'text-[var(--chekin-color-gray-1)]',
                inputType === 'password' &&
                  '[&:not(:placeholder-shown)]:font-bold [&:not(:placeholder-shown)]:tracking-[2px]',
                disabled && 'cursor-not-allowed',
                readOnly && !disabled && !loading && 'cursor-default',
                loading && 'cursor-progress',
                leftIcon && 'pl-10',
                (showRightPaddingForReset || showRightPaddingForReveal) && 'pr-10',
                sign && 'pr-10',
                inputClassName,
              )}
            />
            {sign && (
              <span className="input__sign pointer-events-none absolute right-[14px] top-0 flex h-full items-center text-[18px] font-medium leading-6 text-[var(--chekin-color-brand-navy)]">
                {sign}
              </span>
            )}
            {trailingAdornment && (
              <span className="input__trailing-adornment pointer-events-none absolute right-[14px] top-0 flex h-full items-center">
                {trailingAdornment}
              </span>
            )}
            {onReset && !isEmpty && (
              <button
                type="button"
                onClick={onReset}
                disabled={disabled}
                className="input__reset-button absolute right-0 top-0 flex h-full w-10 items-center justify-center border-0 bg-transparent p-0 text-[var(--chekin-neutral-500)] hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label={t('reset')}
              >
                <X size={14} />
              </button>
            )}
            {isPasswordReveal && (
              <button
                type="button"
                onClick={togglePasswordReveal}
                className="input__password-button absolute right-[14px] top-[18px] flex h-[13px] w-[21px] cursor-pointer items-center justify-center border-0 bg-transparent p-0 hover:opacity-85"
                aria-label={isPasswordRevealed ? t('hide_password') : t('show_password')}
              >
                <Eye
                  size={20}
                  className={cn(
                    'input__password-icon',
                    isPasswordRevealed
                      ? 'text-[var(--chekin-airbnb-pink)]'
                      : 'text-[var(--chekin-color-gray-2)]',
                  )}
                />
              </button>
            )}
          </div>
          {type === 'number' && showNumberButtons && (
            <div className="input__number-controls absolute right-[18px] top-[13px] inline-flex items-center text-right">
              <button
                type="button"
                onClick={onDecrement}
                className="input__decrement-button mr-2 inline-flex h-[23px] w-8 cursor-pointer items-center justify-center rounded-[3px] border-0 bg-[var(--chekin-color-brand-blue)] p-0 text-[20px] font-bold text-white outline-none hover:opacity-90 active:opacity-100"
                aria-label={t('decrement')}
              >
                <Minus size={16} strokeWidth={3} aria-hidden />
              </button>
              <button
                type="button"
                onClick={onIncrement}
                className="input__increment-button inline-flex h-[23px] w-8 cursor-pointer items-center justify-center rounded-[3px] border-0 bg-[var(--chekin-color-brand-blue)] p-0 text-[20px] font-bold text-white outline-none hover:opacity-90 active:opacity-100"
                aria-label={t('increment')}
              >
                <Plus size={16} strokeWidth={3} aria-hidden />
              </button>
            </div>
          )}
        </div>
        {!errorMessage && optional && (
          <span
            data-testid={`${name}-optional`}
            className="input__optional mt-[1px] block text-left text-[14px] font-medium text-[var(--chekin-color-gray-1)]"
          >
            {typeof optional === 'string' ? optional : t('optional')}
          </span>
        )}
        {!errorMessage && helperText && (
          <span className="input__helper-text mt-[1px] block text-[12px] font-normal text-[var(--chekin-color-gray-1)]">
            {helperText}
          </span>
        )}
        {errorMessage && renderErrorMessage && (
          <FieldErrorMessage
            id={errorId}
            message={errorMessage}
            data-testid={name ? `${name}-error` : undefined}
            className="input__error mt-[1px] text-[14px]"
          />
        )}
        {footer}
      </div>
    );
  },
);

Input.displayName = 'Input';

export {Input};
export type {InputProps};
